import { useEffect } from "react";
import { CheckCircle2 } from "lucide-react";

import { MarketingFooter } from "@/components/layout/MarketingFooter";
import { useToast } from "@/hooks/use-toast";
import { DemoHero } from "@/components/demo/DemoHero";
import { DemoCard } from "@/components/demo/DemoCard";
import { DemoFinalCTA } from "@/components/demo/DemoFinalCTA";
import { DemoPersonaSection } from "@/components/demo/DemoPersonaSection";
import { DemoResourcesSection } from "@/components/demo/DemoResourcesSection";
import { HiringLoopSection } from "@/components/demo/HiringLoopSection";
import {
  demoCards,
  hiringLoopSteps,
  outcomeCards,
  personaCards,
  resourceCards,
} from "@/components/demo/demoHubConfig";

const DEMO_META_DESCRIPTION =
  "Explore how theRecruit helps recruiters rank resumes, run AI interviews, compare candidates, and make evidence-backed hiring decisions.";

function useDemoPageMetadata() {
  useEffect(() => {
    const previousTitle = document.title;
    const meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = meta?.getAttribute("content") ?? null;
    const activeMeta = meta ?? document.createElement("meta");

    document.title = "Demo Hub | theRecruit";
    activeMeta.setAttribute("name", "description");
    activeMeta.setAttribute("content", DEMO_META_DESCRIPTION);

    if (!meta) {
      document.head.appendChild(activeMeta);
    }

    return () => {
      document.title = previousTitle;
      if (previousDescription) {
        activeMeta.setAttribute("content", previousDescription);
      }
    };
  }, []);
}

export function DemoHubPage() {
  const { toast } = useToast();
  useDemoPageMetadata();

  const showPlaceholderToast = (title = "Guided demo") => {
    toast({
      title: `${title} is being prepared`,
      description: "Book a live walkthrough with us for now.",
      duration: 4200,
    });
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#f8fafc] text-slate-900 antialiased">
      <DemoHero onPlaceholderClick={() => showPlaceholderToast("90-sec overview video")} />

      <section id="demo-cards" className="bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-[0.44fr_0.56fr] lg:items-end">
            <div>
              <h2 className="font-space-grotesk text-3xl font-bold leading-tight text-slate-950 sm:text-4xl">
                Demo library
              </h2>
              <p className="mt-4 max-w-xl text-base leading-7 text-slate-600">
                All current and upcoming theRecruit demos live here. Pick the walkthrough that
                matches the prospect, team, or sales conversation.
              </p>
            </div>
            <p className="max-w-2xl text-sm leading-6 text-slate-500 lg:justify-self-end">
              Start with the recruiter workflow, then use the related demos to explain interviews,
              candidate experience, decision evidence, or staffing agency workflows.
            </p>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            {demoCards.map((demo) => (
              <DemoCard key={demo.id} demo={demo} onPlaceholderClick={showPlaceholderToast} />
            ))}
          </div>
        </div>
      </section>

      <HiringLoopSection steps={hiringLoopSteps} />
      <DemoPersonaSection personas={personaCards} />

      <section className="bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="font-space-grotesk text-3xl font-bold leading-tight text-slate-950 sm:text-4xl">
              Questions these demos answer
            </h2>
          </div>

          <div className="mt-10 grid gap-px border border-slate-200 bg-slate-200 md:grid-cols-2 lg:grid-cols-3">
            {outcomeCards.map((outcome) => {
              const Icon = outcome.icon;
              return (
                <article key={outcome.title} className="bg-white p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center border border-emerald-200 bg-emerald-50 text-emerald-700">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </div>
                    <div>
                      <CheckCircle2 className="mb-4 h-5 w-5 text-amber-500" aria-hidden="true" />
                      <h3 className="font-space-grotesk text-xl font-semibold leading-tight text-slate-950">
                        {outcome.title}
                      </h3>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <DemoResourcesSection resources={resourceCards} onPlaceholderClick={showPlaceholderToast} />
      <DemoFinalCTA />
      <MarketingFooter />
    </div>
  );
}

export default DemoHubPage;
